import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, GripVertical, X, ArrowUp, ArrowDown, Image as ImageIcon,
  Wand2, Instagram, Twitter, Facebook, Linkedin, Youtube, Globe,
} from "lucide-react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/**
 * Phase 3 site-chrome editors — Header + Footer.
 *
 * Both editors read the current header_config / footer_config from
 * publicPayload (server state) merged with any local unsaved changes,
 * and PATCH the whole object back to /api/business/website via
 * `saveWebsiteContentList({ header_config: {...} })` or
 * `saveWebsiteContentList({ footer_config: {...} })`.
 *
 * The templates re-read from the mutable HEADER/FOOTER exports every
 * render, so the canvas reflects the change immediately once we call
 * `refreshBiz()` — which the parent's `onCanvasChange` triggers after a
 * successful save.
 */

/* -------------------------------------------------------------------------- */
/* Header editor                                                              */
/* -------------------------------------------------------------------------- */

const DEFAULT_NAV = [
  { label: "Home", link: "/" },
  { label: "Services", link: "/services" },
  { label: "About", link: "/about" },
  { label: "Contact", link: "/contact" },
];

export function HeaderEditor({ publicPayload, saveAll, bizId }) {
  const stored = useMemo(() => publicPayload?.website_content?.header_config || {}, [publicPayload]);
  const derived = useMemo(() => publicPayload?.service_config?.header
    || publicPayload?.ecommerce_config?.header
    || publicPayload?.restaurant_config?.header
    || {}, [publicPayload]);

  const [logoText, setLogoText]         = useState(stored.logo_text ?? derived.logo_text ?? "");
  const [logoImageUrl, setLogoImageUrl] = useState(stored.logo_image_url ?? derived.logo_image_url ?? "");
  const [showPhone, setShowPhone]       = useState(stored.show_phone ?? derived.show_phone ?? true);
  const [ctaText, setCtaText]           = useState(stored.cta_text ?? derived.cta_text ?? "");
  const [ctaLink, setCtaLink]           = useState(stored.cta_link ?? derived.cta_link ?? "");
  const [ctaNewTab, setCtaNewTab]       = useState(!!(stored.cta_open_new_tab ?? derived.cta_open_new_tab));
  const [navItems, setNavItems]         = useState(() => {
    const src = Array.isArray(stored.nav_items) && stored.nav_items.length > 0
      ? stored.nav_items
      : (Array.isArray(derived.nav_items) && derived.nav_items.length > 0 ? derived.nav_items : DEFAULT_NAV);
    return src.map((n) => ({ label: n.label || "", link: n.link || "" }));
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.image_url) setLogoImageUrl(data.image_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload logo");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveAll({
        header_config: {
          logo_text: logoText || "",
          logo_image_url: logoImageUrl || "",
          show_phone: !!showPhone,
          cta_text: ctaText || "",
          cta_link: ctaLink || "",
          cta_open_new_tab: !!ctaNewTab,
          nav_items: navItems.filter((n) => (n.label || "").trim()),
        },
      });
      toast.success("Header saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save header");
    } finally {
      setSaving(false);
    }
  };

  const addNav = () => setNavItems((arr) => [...arr, { label: "New link", link: "/" }]);
  const removeNav = (i) => setNavItems((arr) => arr.filter((_, idx) => idx !== i));
  const moveNav = (i, dir) => setNavItems((arr) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return next;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const updateNav = (i, patch) => setNavItems((arr) => arr.map((n, idx) => idx === i ? { ...n, ...patch } : n));

  return (
    <div className="space-y-4" data-testid="header-editor">
      <SubHeading>Logo</SubHeading>
      <Field label="Logo text">
        <Input
          value={logoText}
          onChange={(e) => setLogoText(e.target.value)}
          placeholder="Your business name"
          data-testid="header-editor-logo-text"
        />
      </Field>
      <Field label="Logo image (optional)">
        {logoImageUrl ? (
          <div className="relative w-16 h-16 rounded-full overflow-hidden mb-2 border border-gray-200">
            <img src={logoImageUrl} alt="logo" className="w-full h-full object-cover" />
            <button
              onClick={() => setLogoImageUrl("")}
              className="absolute top-0 right-0 w-4 h-4 rounded-full bg-black text-white flex items-center justify-center text-[9px]"
              data-testid="header-editor-logo-image-clear"
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="flex gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md border border-gray-200 hover:border-black text-xs font-bold"
            data-testid="header-editor-logo-image-upload"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadLogo(e.target.files?.[0])}
          />
        </div>
        <Input
          value={logoImageUrl}
          onChange={(e) => setLogoImageUrl(e.target.value)}
          placeholder="https://…"
          className="mt-2 text-xs"
          data-testid="header-editor-logo-image-url"
        />
      </Field>

      <SubHeading>Navigation</SubHeading>
      <div className="space-y-2" data-testid="header-editor-nav-list">
        {navItems.map((n, i) => (
          <div key={i} className="flex items-center gap-1 group">
            <GripVertical className="w-3 h-3 text-gray-300" />
            <Input
              value={n.label}
              onChange={(e) => updateNav(i, { label: e.target.value })}
              placeholder="Label"
              className="text-xs flex-1"
              data-testid={`header-editor-nav-label-${i}`}
            />
            <Input
              value={n.link}
              onChange={(e) => updateNav(i, { link: e.target.value })}
              placeholder="/link"
              className="text-xs flex-1"
              data-testid={`header-editor-nav-link-${i}`}
            />
            <button
              onClick={() => moveNav(i, -1)}
              disabled={i === 0}
              className="p-1 disabled:opacity-30"
              data-testid={`header-editor-nav-up-${i}`}
              title="Move up"
            >
              <ArrowUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => moveNav(i, 1)}
              disabled={i === navItems.length - 1}
              className="p-1 disabled:opacity-30"
              data-testid={`header-editor-nav-down-${i}`}
              title="Move down"
            >
              <ArrowDown className="w-3 h-3" />
            </button>
            <button
              onClick={() => removeNav(i)}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
              data-testid={`header-editor-nav-remove-${i}`}
              title="Remove"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          onClick={addNav}
          className="w-full py-1.5 border border-dashed border-gray-300 rounded-md text-xs font-bold text-gray-600 hover:border-black hover:text-black flex items-center justify-center gap-1"
          data-testid="header-editor-nav-add"
        >
          <Plus className="w-3 h-3" /> Add nav link
        </button>
      </div>

      <SubHeading>Header CTA</SubHeading>
      <Field label="Button text">
        <Input
          value={ctaText}
          onChange={(e) => setCtaText(e.target.value)}
          placeholder="Book Consultation"
          data-testid="header-editor-cta-text"
        />
      </Field>
      <Field label="Button link">
        <Input
          value={ctaLink}
          onChange={(e) => setCtaLink(e.target.value)}
          placeholder="/consultations"
          data-testid="header-editor-cta-link"
        />
      </Field>
      <div className="flex items-center justify-between text-xs">
        <span>Open in new tab</span>
        <Switch checked={ctaNewTab} onCheckedChange={setCtaNewTab} data-testid="header-editor-cta-newtab" />
      </div>

      <SubHeading>Phone</SubHeading>
      <div className="flex items-center justify-between text-xs">
        <span>Show phone in header</span>
        <Switch checked={showPhone} onCheckedChange={setShowPhone} data-testid="header-editor-show-phone" />
      </div>
      <p className="text-[11px] text-gray-500 leading-relaxed">
        Phone number itself is edited from Site Settings → General.
      </p>

      <div className="pt-3 border-t border-gray-100">
        <Button
          onClick={save}
          disabled={saving}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="header-editor-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Header
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer editor                                                              */
/* -------------------------------------------------------------------------- */

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "twitter",   label: "Twitter / X", Icon: Twitter },
  { key: "facebook",  label: "Facebook",  Icon: Facebook },
  { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin },
  { key: "youtube",   label: "YouTube",   Icon: Youtube },
  { key: "tiktok",    label: "TikTok",    Icon: Globe },
  { key: "pinterest", label: "Pinterest", Icon: Globe },
  { key: "threads",   label: "Threads",   Icon: Globe },
];

// Iter-77 — generic footer link-column editor (ecommerce Help/Company/Legal).
function LinkColumn({ title, items, setItems, testId }) {
  const set = (i, patch) => setItems((arr) => arr.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const move = (i, dir) => setItems((arr) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return next;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  return (
    <div className="space-y-2" data-testid={`${testId}-list`}>
      <div className="text-[11px] font-black uppercase tracking-wider text-gray-500">{title}</div>
      {items.map((q, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input value={q.label} onChange={(e) => set(i, { label: e.target.value })} placeholder="Label" className="text-xs flex-1" data-testid={`${testId}-label-${i}`} />
          <Input value={q.link} onChange={(e) => set(i, { link: e.target.value })} placeholder="/link" className="text-xs flex-1" data-testid={`${testId}-link-${i}`} />
          <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" data-testid={`${testId}-up-${i}`}><ArrowUp className="w-3 h-3" /></button>
          <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 disabled:opacity-30" data-testid={`${testId}-down-${i}`}><ArrowDown className="w-3 h-3" /></button>
          <button onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`${testId}-remove-${i}`}><X className="w-3 h-3" /></button>
        </div>
      ))}
      <button
        onClick={() => setItems((arr) => [...arr, { label: "New link", link: "/" }])}
        className="w-full py-1.5 border border-dashed border-gray-300 rounded-md text-xs font-bold text-gray-600 hover:border-black hover:text-black flex items-center justify-center gap-1"
        data-testid={`${testId}-add`}
      >
        <Plus className="w-3 h-3" /> Add link
      </button>
    </div>
  );
}

export function FooterEditor({ publicPayload, saveAll }) {
  const stored = useMemo(() => publicPayload?.website_content?.footer_config || {}, [publicPayload]);
  const wc = publicPayload?.website_content || {};

  const [tagline, setTagline]     = useState(stored.tagline ?? wc.footer_tagline ?? "");
  const [copyright, setCopyright] = useState(stored.copyright ?? "");
  const [socials, setSocials]     = useState(() =>
    Array.isArray(stored.socials) ? stored.socials.map((s) => ({ platform: s.platform || "instagram", url: s.url || "" })) : []
  );
  const [quickLinks, setQuickLinks] = useState(() =>
    Array.isArray(stored.quick_links) && stored.quick_links.length > 0
      ? stored.quick_links.map((q) => ({ label: q.label || "", link: q.link || "" }))
      : []
  );
  const [showContact, setShowContact] = useState(stored.show_contact !== false);
  const [showAddress, setShowAddress] = useState(stored.show_address !== false);
  const [newsletterEnabled, setNewsletterEnabled] = useState(!!stored.newsletter_enabled);
  const [newsletterHeading, setNewsletterHeading] = useState(stored.newsletter_heading ?? "");
  const [newsletterSubtitle, setNewsletterSubtitle] = useState(stored.newsletter_subtitle ?? "");
  const [newsletterCtaText, setNewsletterCtaText] = useState(stored.newsletter_cta_text ?? "Subscribe");
  // Iter-77 — ecommerce footer link columns (Shop column follows categories).
  const isEcom = !!publicPayload?.ecommerce_config;
  const initLinks = (key, defaults) =>
    Array.isArray(stored[key]) && stored[key].length
      ? stored[key].map((q) => ({ label: q.label || "", link: q.link || "" }))
      : defaults;
  const [helpLinks, setHelpLinks] = useState(() => initLinks("help_links", [
    { label: "Shipping", link: "/shipping" },
    { label: "Returns", link: "/returns" },
    { label: "Contact", link: "/contact" },
    { label: "FAQ", link: "/faq" },
  ]));
  const [companyLinks, setCompanyLinks] = useState(() => initLinks("company_links", [
    { label: "About", link: "/about" },
    { label: "Support", link: "/support" },
  ]));
  const [legalLinks, setLegalLinks] = useState(() => initLinks("legal_links", [
    { label: "Privacy", link: "/privacy" },
    { label: "Terms", link: "/terms" },
  ]));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await saveAll({
        footer_config: {
          tagline: tagline || "",
          copyright: copyright || "",
          socials: socials.filter((s) => (s.url || "").trim()),
          quick_links: quickLinks.filter((q) => (q.label || "").trim()),
          show_contact: !!showContact,
          show_address: !!showAddress,
          newsletter_enabled: !!newsletterEnabled,
          newsletter_heading: newsletterHeading || "",
          newsletter_subtitle: newsletterSubtitle || "",
          newsletter_cta_text: newsletterCtaText || "Subscribe",
          ...(isEcom ? {
            help_links: helpLinks.filter((q) => (q.label || "").trim()),
            company_links: companyLinks.filter((q) => (q.label || "").trim()),
            legal_links: legalLinks.filter((q) => (q.label || "").trim()),
          } : {}),
        },
      });
      toast.success("Footer saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save footer");
    } finally {
      setSaving(false);
    }
  };

  const addSocial = () => setSocials((arr) => [...arr, { platform: "instagram", url: "" }]);
  const removeSocial = (i) => setSocials((arr) => arr.filter((_, idx) => idx !== i));
  const updateSocial = (i, patch) => setSocials((arr) => arr.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const addQuick = () => setQuickLinks((arr) => [...arr, { label: "New link", link: "/" }]);
  const removeQuick = (i) => setQuickLinks((arr) => arr.filter((_, idx) => idx !== i));
  const updateQuick = (i, patch) => setQuickLinks((arr) => arr.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const moveQuick = (i, dir) => setQuickLinks((arr) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return next;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  return (
    <div className="space-y-4" data-testid="footer-editor">
      <Field label="Tagline">
        <Textarea
          rows={3}
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="A short blurb about your business…"
          data-testid="footer-editor-tagline"
        />
      </Field>

      <SubHeading>Social links</SubHeading>
      <div className="space-y-2" data-testid="footer-editor-social-list">
        {socials.map((s, i) => (
          <div key={i} className="flex items-center gap-1">
            <select
              value={s.platform}
              onChange={(e) => updateSocial(i, { platform: e.target.value })}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5"
              data-testid={`footer-editor-social-platform-${i}`}
            >
              {SOCIAL_PLATFORMS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            <Input
              value={s.url}
              onChange={(e) => updateSocial(i, { url: e.target.value })}
              placeholder="https://…"
              className="text-xs flex-1"
              data-testid={`footer-editor-social-url-${i}`}
            />
            <button
              onClick={() => removeSocial(i)}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
              data-testid={`footer-editor-social-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          onClick={addSocial}
          className="w-full py-1.5 border border-dashed border-gray-300 rounded-md text-xs font-bold text-gray-600 hover:border-black hover:text-black flex items-center justify-center gap-1"
          data-testid="footer-editor-social-add"
        >
          <Plus className="w-3 h-3" /> Add social link
        </button>
      </div>

      <SubHeading>Quick links</SubHeading>
      <div className="space-y-2" data-testid="footer-editor-quick-list">
        {quickLinks.map((q, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input
              value={q.label}
              onChange={(e) => updateQuick(i, { label: e.target.value })}
              placeholder="Label"
              className="text-xs flex-1"
              data-testid={`footer-editor-quick-label-${i}`}
            />
            <Input
              value={q.link}
              onChange={(e) => updateQuick(i, { link: e.target.value })}
              placeholder="/link"
              className="text-xs flex-1"
              data-testid={`footer-editor-quick-link-${i}`}
            />
            <button onClick={() => moveQuick(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" data-testid={`footer-editor-quick-up-${i}`}>
              <ArrowUp className="w-3 h-3" />
            </button>
            <button onClick={() => moveQuick(i, 1)} disabled={i === quickLinks.length - 1} className="p-1 disabled:opacity-30" data-testid={`footer-editor-quick-down-${i}`}>
              <ArrowDown className="w-3 h-3" />
            </button>
            <button onClick={() => removeQuick(i)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`footer-editor-quick-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          onClick={addQuick}
          className="w-full py-1.5 border border-dashed border-gray-300 rounded-md text-xs font-bold text-gray-600 hover:border-black hover:text-black flex items-center justify-center gap-1"
          data-testid="footer-editor-quick-add"
        >
          <Plus className="w-3 h-3" /> Add quick link
        </button>
      </div>

      {isEcom && (
        <>
          <SubHeading>Footer columns (Help / Company / Legal)</SubHeading>
          <p className="text-[11px] text-gray-500 leading-relaxed">
            The Shop column follows your product categories. Column headings are
            editable by clicking them on the canvas.
          </p>
          <LinkColumn title="Help links" items={helpLinks} setItems={setHelpLinks} testId="footer-editor-help" />
          <LinkColumn title="Company links" items={companyLinks} setItems={setCompanyLinks} testId="footer-editor-company" />
          <LinkColumn title="Legal links" items={legalLinks} setItems={setLegalLinks} testId="footer-editor-legal" />
        </>
      )}

      <SubHeading>Contact display</SubHeading>
      <div className="flex items-center justify-between text-xs">
        <span>Show contact block</span>
        <Switch checked={showContact} onCheckedChange={setShowContact} data-testid="footer-editor-show-contact" />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span>Show address in contact block</span>
        <Switch checked={showAddress} onCheckedChange={setShowAddress} data-testid="footer-editor-show-address" />
      </div>
      <p className="text-[11px] text-gray-500 leading-relaxed">
        Phone / email / address themselves are edited from Site Settings → General.
      </p>

      <SubHeading>Newsletter</SubHeading>
      <div className="flex items-center justify-between text-xs">
        <span>Show newsletter signup</span>
        <Switch checked={newsletterEnabled} onCheckedChange={setNewsletterEnabled} data-testid="footer-editor-newsletter-enabled" />
      </div>
      {newsletterEnabled && (
        <>
          <Field label="Newsletter heading">
            <Input
              value={newsletterHeading}
              onChange={(e) => setNewsletterHeading(e.target.value)}
              placeholder="Stay in the loop"
              data-testid="footer-editor-newsletter-heading"
            />
          </Field>
          <Field label="Newsletter subtitle">
            <Input
              value={newsletterSubtitle}
              onChange={(e) => setNewsletterSubtitle(e.target.value)}
              placeholder="Get updates, deals, and helpful tips."
              data-testid="footer-editor-newsletter-subtitle"
            />
          </Field>
          <Field label="Newsletter button text">
            <Input
              value={newsletterCtaText}
              onChange={(e) => setNewsletterCtaText(e.target.value)}
              placeholder="Subscribe"
              data-testid="footer-editor-newsletter-cta"
            />
          </Field>
        </>
      )}

      <Field label="Copyright text (optional)">
        <Input
          value={copyright}
          onChange={(e) => setCopyright(e.target.value)}
          placeholder="© 2026 Business Name."
          data-testid="footer-editor-copyright"
        />
      </Field>

      <div className="pt-3 border-t border-gray-100">
        <Button
          onClick={save}
          disabled={saving}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="footer-editor-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Footer
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function SubHeading({ children }) {
  return (
    <div className="text-[11px] font-black uppercase tracking-widest text-gray-500 mt-3">
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
